import React, { useMemo } from 'react';
import { AnalysisResult } from '../../contracts/scoring';
import { PostureGrid } from '../../components/PostureGrid';
import { AdviceCard } from '../../components/SmartTrainer/AdviceCard';

type MetricCard = { label: string; value: string; hint?: string };

const safeNum = (n: any) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : null;
};

const clamp0to100 = (n: number) => Math.max(0, Math.min(100, n));

const fmt = (n: any, decimals = 0) => {
  const x = safeNum(n);
  if (x === null) return '—';
  return decimals > 0 ? x.toFixed(decimals) : String(Math.round(x));
};

export const PostureTab: React.FC<{
  result: AnalysisResult;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}> = ({ result, isFullscreen, onToggleFullscreen }) => {
  const bio = result.biomechanics;

  const rawScore = safeNum(result.session.metrics.posture);
  const score = clamp0to100(Math.round(rawScore ?? 0));

  const diagnostics = result.feedback.diagnostics.filter(d => d.domain === 'POSTURE');
  const dbg = (result.session.metrics.debug || {}) as any;

  const refUrl = result.alignedUrls?.ref || '';
  const stuUrl = result.alignedUrls?.stu || '';

  const stuEnvelope = result.rhythm?.stuEnvelope || [];

  const onsets = Array.isArray(dbg.onsetTimesMs)
    ? (dbg.onsetTimesMs as number[]).map(ms => ms / 1000)
    : [];

  const measuredCards: MetricCard[] = useMemo(() => {
    const cards: MetricCard[] = [];

    cards.push({ label: 'Posture score', value: `${fmt(score)}%`, hint: 'Plus haut = mieux' });

    // On n’invente rien : on expose seulement ce qu’on a
    cards.push({
      label: 'Onsets',
      value: onsets.length ? `${onsets.length} pts` : '—',
      hint: 'Impacts détectés',
    });

    cards.push({
      label: 'Stu envelope',
      value: stuEnvelope.length ? `${stuEnvelope.length} pts` : '—',
      hint: 'Enveloppe élève',
    });

    // Si le moteur expose des signaux vision (selon versions)
    if (safeNum(dbg.vision_noise) !== null) {
      cards.push({
        label: 'Vision noise',
        value: fmt(dbg.vision_noise, 2),
        hint: 'Bruit vidéo estimé',
      });
    }

    if (safeNum(dbg.motion_index) !== null) {
      cards.push({
        label: 'Motion index',
        value: fmt(dbg.motion_index, 2),
        hint: 'Indice de mouvement',
      });
    }

    cards.push({
      label: 'Video',
      value: stuUrl ? 'OK' : '—',
      hint: 'URL élève disponible',
    });

    return cards.slice(0, 6);
  }, [score, onsets.length, stuEnvelope.length, dbg, stuUrl]);

  return (
    <div className={`flex flex-col gap-10 animate-fade-in ${isFullscreen ? 'h-full' : 'pb-12'}`}>
      {!isFullscreen && (
        <>
          {/* 1) SCORE + POURQUOI + CONSEILS */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4 bg-[#02080a] p-10 rounded-[48px] border border-white/10 shadow-2xl flex flex-col items-center text-center justify-center gap-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"></div>
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] italic">Anatomy Score</span>
              <div className="flex items-baseline gap-2">
                <span className={`text-8xl font-black italic tracking-tighter ${score > 75 ? 'text-emerald-400' : 'text-cyan-400'}`}>
                  {score}
                </span>
                <span className="text-xl font-bold text-slate-700">/100</span>
              </div>
              <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-[9px] font-black text-slate-400 uppercase tracking-widest italic text-center">
                {score > 70 ? 'Geste Économe ✓' : 'Instabilité ou Bruit Visuel'}
              </div>
            </div>

            <div className="lg:col-span-8 bg-black/40 p-8 rounded-[48px] border border-white/10 flex flex-col justify-center gap-6 shadow-inner relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-5 text-6xl font-black italic select-none">BIOMETRIC</div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-2xl">👁️</div>
                <h4 className="text-sm font-black text-white uppercase italic tracking-widest">Pourquoi ce score ?</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                <div className="space-y-4">
                  {/* ✅ React-friendly (pas de <br/> dans une string) */}
                  <p className="text-sm text-slate-300 italic leading-relaxed pl-4 border-l-2 border-cyan-500/40">
                    "Le Vision Lab mesure l’économie de mouvement.
                    Tout mouvement non lié au jeu est interprété comme tension / instabilité."
                  </p>
                  <p className="text-sm text-slate-300 italic leading-relaxed pl-4 border-l-2 border-cyan-500/40">
                    "Si votre score chute malgré une bonne posture : vérifiez la lumière.
                    Une pièce sombre crée du bruit vidéo (grain) que l’IA peut confondre avec du mouvement."
                  </p>
                </div>

                <div className="bg-black/60 p-5 rounded-3xl border border-white/5 space-y-3 shadow-xl">
                  <h5 className="text-[10px] font-black text-smart-gold uppercase tracking-widest">Facteurs d’échec :</h5>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-[10px] text-slate-400">
                      <span className="text-red-500 font-black">●</span> Pièce trop sombre (bruit vidéo)
                    </li>
                    <li className="flex items-center gap-2 text-[10px] text-slate-400">
                      <span className="text-red-500 font-black">●</span> Épaules qui montent (tension)
                    </li>
                    <li className="flex items-center gap-2 text-[10px] text-slate-400">
                      <span className="text-red-500 font-black">●</span> Balancement de tête excessif
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-4 space-y-3 relative z-10">
                {diagnostics.length > 0 ? (
                  diagnostics.map((adv, i) => <AdviceCard key={i} advice={adv} isHighContrast={false} />)
                ) : (
                  <div className="p-6 bg-white/5 rounded-3xl text-center">
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                      Aucun signal d’alerte majeur détecté
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2) MESURES DÉTECTÉES (standard) */}
          <div className="bg-white/[0.02] border border-white/10 p-8 rounded-[48px] space-y-6">
            <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Mesures détectées</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {measuredCards.map((m, i) => (
                <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{m.label}</div>
                  <div className="text-2xl font-black italic text-white tabular-nums">{m.value}</div>
                  {m.hint ? <div className="text-[9px] mt-2 italic text-slate-500">{m.hint}</div> : null}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 3) MODULE */}
      <div className={`${isFullscreen ? 'h-full w-full' : 'h-[600px]'}`}>
        <PostureGrid
          data={bio || undefined}
          refVideoUrl={refUrl}
          stuVideoUrl={stuUrl}
          onsets={onsets}
          stuEnvelope={stuEnvelope}
          isFullscreen={isFullscreen}
          onToggleFullscreen={onToggleFullscreen}
        />
      </div>
    </div>
  );
};
